import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useGlobal } from '../Context/GlobalContext';
import { useAuth } from '../Auth/AuthContext';
import { WorkspaceCache } from '../utils/cache';

const WorkspaceContext = createContext();

export const WorkspaceProvider = ({ children }) => {
  const { apiUrl } = useGlobal();
  const { isLoggedIn } = useAuth();

  // Initialize workspace state with optimistic data from cache
  const [currentWorkspace, setCurrentWorkspace] = useState(() => {
    // First try to get from cache for immediate rendering
    const cachedData = WorkspaceCache.getOptimisticData();

    if (cachedData?.currentWorkspaceId) {
      // Find workspace in cached list if available
      const workspace = cachedData.workspacesList.find(
        ws => ws.workspace_id === cachedData.currentWorkspaceId
      );

      if (workspace) {
        return {
          workspace_id: workspace.workspace_id,
          role: workspace.role,
          name: workspace.name
        };
      }
    }

    // Fallback to localStorage
    const storedWorkspaceId = localStorage.getItem('current_workspace_id');
    const storedRole = localStorage.getItem('current_user_role');
    const storedWorkspaceName = localStorage.getItem('current_workspace_name');

    if (storedWorkspaceId) {
      return {
        workspace_id: storedWorkspaceId,
        role: storedRole || 'user',
        name: storedWorkspaceName || 'Workspace'
      };
    }
    return null;
  });

  // Initialize workspace data with cached data for optimistic loading
  const [workspaceData, setWorkspaceData] = useState(() => {
    const cachedData = WorkspaceCache.getOptimisticData();
    return cachedData?.workspaceData || null;
  });

  const [workspaces, setWorkspaces] = useState(() => {
    const cachedData = WorkspaceCache.getOptimisticData();
    return cachedData?.workspacesList || [];
  });

  // Separate loading states for different operations
  const [loadingStates, setLoadingStates] = useState({
    fetchingWorkspaces: false,
    switchingWorkspace: false,
    creatingWorkspace: false,
    updatingWorkspace: false,
    deletingWorkspace: false,
    fetchingDetails: false,
  });

  // Computed loading state for backward compatibility
  const loading = Object.values(loadingStates).some(Boolean);

  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [lastUserEmail, setLastUserEmail] = useState(() => localStorage.getItem('user_email'));
  const [needsSubscriptionExpiredRedirect, setNeedsSubscriptionExpiredRedirect] = useState(false);

  // Utility functions for managing loading states
  const setLoadingState = (operation, isLoading) => {
    setLoadingStates(prev => ({ ...prev, [operation]: isLoading }));
  };

  // Utility function to parse common API errors
  const parseApiError = (error, operation = 'operation') => {
    if (error.name === 'NetworkError' || !navigator.onLine) {
      return `Network error. Unable to ${operation}. Please check your connection and try again.`;
    }

    const message = error.message?.toLowerCase() || '';
    if (message.includes('401') || message.includes('unauthorized')) {
      return 'Your session has expired. Please log in again.';
    }
    if (message.includes('403') || message.includes('forbidden')) {
      return `Access denied. You don't have permission to ${operation}.`;
    }
    if (message.includes('404') || message.includes('not found')) {
      return `Resource not found. It may have been deleted or moved.`;
    }
    if (message.includes('409') || message.includes('conflict')) {
      return 'A conflict occurred. The resource may already exist.';
    }
    if (message.includes('429') || message.includes('too many requests')) {
      return `Rate limit exceeded. Please wait a moment before trying again.`;
    }
    if (message.includes('limit') || message.includes('quota')) {
      return `Limit reached. Please upgrade your plan to continue.`;
    }

    // Return the original error message if it's user-friendly, otherwise return a generic message
    return error.message && error.message.length < 100 ? error.message : `Failed to ${operation}. Please try again.`;
  };

  // Clear error when operations succeed
  const clearError = () => setError(null);

  // Track if we're using cached data that needs refreshing
  const [isUsingCachedData, setIsUsingCachedData] = useState(() => {
    const cachedData = WorkspaceCache.getOptimisticData();
    return !!(cachedData?.workspaceData || cachedData?.workspacesList?.length);
  });

  // Rate limiting state
  const [lastApiCall, setLastApiCall] = useState(0);
  const [rateLimitBackoff, setRateLimitBackoff] = useState(0);
  const MIN_API_INTERVAL = 1000; // Minimum 1 second between API calls

  // Circuit breaker state
  const [failureCount, setFailureCount] = useState({});
  const [blockedOperations, setBlockedOperations] = useState(new Set());
  const FAILURE_THRESHOLD = 3; // Number of failures before blocking
  const CIRCUIT_BREAKER_TIMEOUT = 30000; // 30 seconds

  // Request timeout and abort controller state
  const [, setActiveRequests] = useState(new Map());
  const REQUEST_TIMEOUT = 30000; // 30 seconds

  // Debounce utility for API calls with circuit breaker
  const debounceApiCall = useCallback(async (apiFunction, operationName, ...args) => {
    const now = Date.now();

    // Check circuit breaker
    if (blockedOperations.has(operationName)) {
      const lastFailure = failureCount[operationName]?.lastFailure || 0;
      if (now - lastFailure < CIRCUIT_BREAKER_TIMEOUT) {
        throw new Error(`Operation ${operationName} is temporarily blocked due to repeated failures`);
      } else {
        // Reset circuit breaker after timeout
        setBlockedOperations(prev => {
          const next = new Set(prev);
          next.delete(operationName);
          return next;
        });
        setFailureCount(prev => ({ ...prev, [operationName]: { count: 0, lastFailure: 0 } }));
      }
    }

    const timeSinceLastCall = now - lastApiCall;
    const backoffTime = Math.max(rateLimitBackoff - now, 0);

    if (timeSinceLastCall < MIN_API_INTERVAL || backoffTime > 0) {
      const waitTime = Math.max(MIN_API_INTERVAL - timeSinceLastCall, backoffTime);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    setLastApiCall(Date.now());

    // Create abort controller for timeout
    const controller = new AbortController();
    const requestId = `${operationName}-${Date.now()}`;

    // Set timeout
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, REQUEST_TIMEOUT);

    // Track active request
    setActiveRequests(prev => new Map(prev).set(requestId, controller));

    try {
      const result = await apiFunction(controller.signal, ...args);
      setRateLimitBackoff(0); // Reset backoff on success

      // Reset failure count on success
      if (failureCount[operationName]?.count > 0) {
        setFailureCount(prev => ({ ...prev, [operationName]: { count: 0, lastFailure: 0 } }));
      }

      return result;
    } catch (error) {
      // Don't count aborted requests as failures
      if (error.name === 'AbortError') {
        throw new Error(`Request ${operationName} timed out`);
      }
      // Track failures for circuit breaker
      const currentFailures = (failureCount[operationName]?.count || 0) + 1;
      setFailureCount(prev => ({
        ...prev,
        [operationName]: { count: currentFailures, lastFailure: Date.now() }
      }));

      if (currentFailures >= FAILURE_THRESHOLD) {
        setBlockedOperations(prev => new Set(prev).add(operationName));
      }

      // Handle 429 rate limiting with exponential backoff
      if (error.message?.includes('429') || error.message?.toLowerCase().includes('too many requests')) {
        const backoffDelay = Math.min(30000, 5000 * Math.pow(2, Math.floor(rateLimitBackoff / 5000))); // Max 30 seconds
        setRateLimitBackoff(Date.now() + backoffDelay);
      }
      throw error;
    } finally {
      // Clean up
      clearTimeout(timeoutId);
      setActiveRequests(prev => {
        const next = new Map(prev);
        next.delete(requestId);
        return next;
      });
    }
  }, [lastApiCall, rateLimitBackoff, failureCount, blockedOperations]);

  // Fetch user's workspaces
  const fetchWorkspaces = useCallback(async (forceRefresh = false) => {
    try {
      const token = localStorage.getItem('brandvox_token');

      if (!token) {
        setWorkspaces([]);
        WorkspaceCache.clearAll();
        return [];
      }

      // Check cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedWorkspaces = WorkspaceCache.getWorkspacesList();
        if (cachedWorkspaces && !cachedWorkspaces.isStale) {
          setWorkspaces(cachedWorkspaces.data);
          setIsUsingCachedData(false);
          return cachedWorkspaces.data;
        }
      }

      setLoadingState('fetchingWorkspaces', true);
      clearError();

      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/workspace/get-user-workspaces`, {
          signal,
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }, 'fetchWorkspaces');

      if (response.ok) {
        const data = await response.json();
        const workspacesList = data.workspaces || [];

        // Update state and cache
        setWorkspaces(workspacesList);
        WorkspaceCache.setWorkspacesList(workspacesList);
        setIsUsingCachedData(false);

        return workspacesList;
      } else {
        setWorkspaces([]);
        return [];
      }
    } catch (error) {
      setError(parseApiError(error, 'load workspaces'));

      // Try to use cached data as fallback
      const cachedWorkspaces = WorkspaceCache.getWorkspacesList();
      if (cachedWorkspaces) {
        setWorkspaces(cachedWorkspaces.data);
        return cachedWorkspaces.data;
      }

      setWorkspaces([]);
      return [];
    } finally {
      setLoadingState('fetchingWorkspaces', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  // Switch to a different workspace
  const switchWorkspace = useCallback(async (workspaceId) => {
    try {
      setLoadingState('switchingWorkspace', true);
      clearError();
      const token = localStorage.getItem('brandvox_token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/workspace/switch-workspace`, {
          signal,
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ workspace_id: workspaceId }),
        });
      }, 'switchWorkspace');

      if (response.ok) {
        const data = await response.json();

        // Clear cache for workspace data when switching
        WorkspaceCache.clearWorkspaceData(workspaceId);

        // Update local storage with new workspace context
        localStorage.setItem('current_workspace_id', data.workspace_id);
        localStorage.setItem('current_workspace_name', data.name);

        // Clear previous workspace data immediately to prevent showing old data
        setWorkspaceData(null);

        // Get the user's role in this workspace from the workspaces list
        const workspace = workspaces.find(ws => ws.workspace_id === workspaceId);
        if (workspace) {
          localStorage.setItem('current_user_role', workspace.role);

          // Update current workspace state
          setCurrentWorkspace({
            workspace_id: data.workspace_id,
            name: data.name,
            role: workspace.role
          });

          // Fetch new workspace details to get chatbots and other data
          await fetchWorkspaceDetails(workspaceId, true);
        } else {
          // Fallback: fetch workspace details to get role
          await fetchWorkspaceDetails(workspaceId, true);
        }

        return { success: true, workspace: data };
      } else {
        const errorData = await response.json();

        // Handle 403 specifically - user doesn't have access to workspace
        if (response.status === 403) {
          // Clear the invalid workspace from localStorage to prevent retry loops
          localStorage.removeItem('current_workspace_id');
          localStorage.removeItem('current_workspace_name');
          localStorage.removeItem('current_user_role');

          // Try to switch to first available workspace from user's workspace list
          if (workspaces.length > 0) {
            const firstValidWorkspace = workspaces[0];

            // Prevent infinite recursion - check if we're already trying to switch to this workspace
            if (firstValidWorkspace.workspace_id === workspaceId) {
              clearWorkspace();
              throw new Error('Invalid workspace access - context cleared');
            }

            return await switchWorkspace(firstValidWorkspace.workspace_id);
          }

          // If no workspaces available, clear everything
          clearWorkspace();
          throw new Error('No accessible workspaces found');
        }

        throw new Error(errorData.detail || 'Failed to switch workspace');
      }
    } catch (error) {
      const errorMessage = parseApiError(error, 'switch workspace');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoadingState('switchingWorkspace', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, workspaces]);

  // Fetch details of a specific workspace
  const fetchWorkspaceDetails = useCallback(async (workspaceId, forceRefresh = false) => {
    try {
      const token = localStorage.getItem('brandvox_token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      // Check cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedWorkspaceData = WorkspaceCache.getWorkspaceData(workspaceId);
        if (cachedWorkspaceData && !cachedWorkspaceData.isStale) {
          const data = cachedWorkspaceData.data;

          const workspaceInfo = {
            workspace_id: data.workspace_id,
            name: data.name,
            role: data.user_role || 'user',
          };

          setCurrentWorkspace(workspaceInfo);
          setWorkspaceData(data);
          setIsUsingCachedData(false);

          return { workspaceInfo, workspaceData: data };
        }
      }

      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/workspace/get-workspace?workspace_id=${workspaceId}`, {
          signal,
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }, 'fetchWorkspaceDetails');

      if (response.ok) {
        const data = await response.json();

        // Update current workspace with fetched details
        const workspaceInfo = {
          workspace_id: data.workspace_id,
          name: data.name,
          role: data.user_role || 'user', // Use user_role from API response
        };

        setCurrentWorkspace(workspaceInfo);
        setWorkspaceData(data); // Store full workspace data including chatbots and quick_start_guide

        // Cache the workspace data
        WorkspaceCache.setWorkspaceData(workspaceId, data);

        // Update localStorage
        localStorage.setItem('current_workspace_id', data.workspace_id);
        localStorage.setItem('current_workspace_name', data.name);
        localStorage.setItem('current_user_role', data.user_role || 'user');

        setIsUsingCachedData(false);
        return { workspaceInfo, workspaceData: data };
      } else {
        // Handle 403 specifically - user doesn't have access to workspace
        if (response.status === 403) {

          // Clear the invalid workspace from localStorage to prevent retry loops
          localStorage.removeItem('current_workspace_id');
          localStorage.removeItem('current_workspace_name');
          localStorage.removeItem('current_user_role');

          // Clear workspace data
          setWorkspaceData(null);

          // Try to switch to first available workspace from user's workspace list
          if (workspaces.length > 0) {
            const firstValidWorkspace = workspaces[0];

            // Prevent infinite recursion - check if we're already trying to fetch details for this workspace
            if (firstValidWorkspace.workspace_id === workspaceId) {
              // Clear workspace context inline to avoid circular dependency
              setCurrentWorkspace(null);
              setWorkspaceData(null);
              setWorkspaces([]);
              setIsUsingCachedData(false);
              localStorage.removeItem('current_workspace_id');
              localStorage.removeItem('current_workspace_name');
              localStorage.removeItem('current_user_role');
              WorkspaceCache.clearAll();
              throw new Error('Invalid workspace access - context cleared');
            }

            // Set the first valid workspace as current
            setCurrentWorkspace({
              workspace_id: firstValidWorkspace.workspace_id,
              name: firstValidWorkspace.name,
              role: firstValidWorkspace.role
            });

            // Update localStorage
            localStorage.setItem('current_workspace_id', firstValidWorkspace.workspace_id);
            localStorage.setItem('current_workspace_name', firstValidWorkspace.name);
            localStorage.setItem('current_user_role', firstValidWorkspace.role);

            // Fetch details for the new valid workspace
            return await fetchWorkspaceDetails(firstValidWorkspace.workspace_id, true);
          }

          // If no workspaces available, clear everything
          setCurrentWorkspace(null);
          setWorkspaceData(null);
          setWorkspaces([]);
          setIsUsingCachedData(false);
          localStorage.removeItem('current_workspace_id');
          localStorage.removeItem('current_workspace_name');
          localStorage.removeItem('current_user_role');
          WorkspaceCache.clearAll();
          throw new Error('No accessible workspaces found');
        }

        throw new Error('Failed to fetch workspace details');
      }
    } catch (error) {
      // Don't set generic error for workspace access issues that we've already handled
      if (!error.message?.includes('Invalid workspace access') && !error.message?.includes('No accessible workspaces found')) {
        setError(parseApiError(error, 'fetch workspace details'));
      }

      // Try to use cached data as fallback only if it's not a 403 access error
      if (!error.message?.includes('Invalid workspace access') && !error.message?.includes('No accessible workspaces found')) {
        const cachedWorkspaceData = WorkspaceCache.getWorkspaceData(workspaceId);
        if (cachedWorkspaceData) {
          const data = cachedWorkspaceData.data;
          const workspaceInfo = {
            workspace_id: data.workspace_id,
            name: data.name,
            role: data.user_role || 'user',
          };
          setCurrentWorkspace(workspaceInfo);
          setWorkspaceData(data);
          return workspaceInfo;
        }
      }

      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, workspaces]);

  // Create a new workspace
  const createWorkspace = useCallback(async (name, brandName, workspace_language = 'en') => {
    try {
      setLoadingState('creatingWorkspace', true);
      clearError();
      const token = localStorage.getItem('brandvox_token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await debounceApiCall(async () => {
        return fetch(`${apiUrl}/api/v1/workspace/create-workspace`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name,
            brand_name: brandName,
            workspace_language
          }),
        });
      }, 'createWorkspace');

      if (response.ok) {
        const data = await response.json();

        // Clear cache immediately to ensure fresh data
        WorkspaceCache.clearAll();

        // Refresh workspaces list with force refresh
        await fetchWorkspaces(true);

        // Switch to the newly created workspace
        await switchWorkspace(data.workspace_id);

        return { success: true, workspace_id: data.workspace_id };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create workspace');
      }
    } catch (error) {
      const errorMessage = parseApiError(error, 'create workspace');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoadingState('creatingWorkspace', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, fetchWorkspaces, switchWorkspace]);

  // Update workspace details
  const updateWorkspace = useCallback(async (workspaceId, updates) => {
    try {
      setLoadingState('updatingWorkspace', true);
      clearError();
      const token = localStorage.getItem('brandvox_token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await debounceApiCall(async () => {
        return fetch(`${apiUrl}/api/v1/workspace/update-workspace`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            workspace_id: workspaceId,
            ...updates
          }),
        });
      }, 'updateWorkspace');

      if (response.ok) {
        const data = await response.json();

        // Clear cache to ensure fresh data
        WorkspaceCache.clearAll();

        // Update current workspace if it's the one being updated
        if (currentWorkspace?.workspace_id === workspaceId) {
          setCurrentWorkspace(prev => ({
            ...prev,
            name: data.name || prev.name,
            description: data.description
          }));

          if (data.name) {
            localStorage.setItem('current_workspace_name', data.name);
          }
        }

        // Refresh workspaces list with force refresh
        await fetchWorkspaces(true);

        return { success: true, workspace: data };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update workspace');
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoadingState('updatingWorkspace', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, currentWorkspace, fetchWorkspaces]);

  // Delete a workspace
  const deleteWorkspace = useCallback(async (workspaceId) => {
    try {
      setLoadingState('deletingWorkspace', true);
      clearError();
      const token = localStorage.getItem('brandvox_token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await debounceApiCall(async () => {
        return fetch(`${apiUrl}/api/v1/workspace/delete`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            workspace_id: workspaceId
          }),
        });
      }, 'deleteWorkspace');

      if (response.ok) {
        await response.json();

        // Clear cache immediately to ensure fresh data
        WorkspaceCache.clearAll();

        // Refresh workspaces list with force refresh
        const updatedWorkspaces = await fetchWorkspaces(true);

        // If deleted workspace was current, switch to first available
        if (currentWorkspace?.workspace_id === workspaceId && updatedWorkspaces.length > 0) {
          await switchWorkspace(updatedWorkspaces[0].workspace_id);
        } else if (updatedWorkspaces.length === 0) {
          // No workspaces left, clear current workspace
          clearWorkspace();
        }

        // Trigger user data refresh to update workspace count
        // This will be handled by the component that calls this function
        return { success: true, shouldRefreshUser: true };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete workspace');
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoadingState('deletingWorkspace', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, currentWorkspace, fetchWorkspaces, switchWorkspace]);

  // Check workspace subscriptions and switch to first active one
  const checkWorkspaceSubscriptions = useCallback(async () => {
    try {
      const token = localStorage.getItem('brandvox_token');
      if (!token) return false;

      // Fetch all user workspaces with subscription status
      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/workspace/get-user-workspaces`, {
          signal,
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }, 'checkWorkspaceSubscriptions');

      if (response.ok) {
        const data = await response.json();
        const workspacesList = data.workspaces || [];

        // Find first workspace with active creator subscription
        const activeWorkspace = workspacesList.find(ws => ws.creator_has_active_subscription);

        if (activeWorkspace) {
          // Switch to the active workspace
          localStorage.setItem('current_workspace_id', activeWorkspace.workspace_id);
          localStorage.setItem('current_workspace_name', activeWorkspace.name);
          localStorage.setItem('current_user_role', activeWorkspace.role);
          localStorage.setItem('needs_subscription_check', 'false');

          setCurrentWorkspace({
            workspace_id: activeWorkspace.workspace_id,
            name: activeWorkspace.name,
            role: activeWorkspace.role
          });

          setWorkspaces(workspacesList);
          WorkspaceCache.setWorkspacesList(workspacesList);

          // Fetch workspace details
          await fetchWorkspaceDetails(activeWorkspace.workspace_id, true);
          return true;
        } else {
          // No workspace with active subscription found
          setWorkspaces(workspacesList);
          setNeedsSubscriptionExpiredRedirect(true);
          return false;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [apiUrl, fetchWorkspaceDetails, debounceApiCall]);

  // Clear workspace context (used on logout)
  const clearWorkspace = useCallback(() => {
    setCurrentWorkspace(null);
    setWorkspaceData(null);
    setWorkspaces([]);
    setIsUsingCachedData(false);
    setNeedsSubscriptionExpiredRedirect(false);
    localStorage.removeItem('current_workspace_id');
    localStorage.removeItem('current_workspace_name');
    localStorage.removeItem('current_user_role');
    localStorage.removeItem('needs_subscription_check');
    WorkspaceCache.clearAll();
  }, []);

  // Initialize workspace context on mount or auth change
  // Use ref to track initialization without causing re-renders
  const initializedRef = useRef(initialized);

  useEffect(() => {
    initializedRef.current = initialized;
  }, [initialized]);

  const initializeWorkspace = useCallback(async () => {

    if (initializedRef.current) {
      return;
    }

    const token = localStorage.getItem('brandvox_token');

    if (!token) {
      clearWorkspace();
      setInitialized(true);
      return;
    }

    try {
      const storedWorkspaceId = localStorage.getItem('current_workspace_id');
      const needsSubscriptionCheck = localStorage.getItem('needs_subscription_check') === 'true';

      // If subscription check is needed, perform it first
      if (needsSubscriptionCheck) {
        const hasActiveWorkspace = await checkWorkspaceSubscriptions();
        if (hasActiveWorkspace) {
          setInitialized(true);
          return;
        }
        // If no active workspace found, continue with normal flow but flag for redirect
      }

      // If we already have cached data, use it and fetch fresh data in background
      const cachedData = WorkspaceCache.getOptimisticData();
      const shouldBackgroundRefresh = isUsingCachedData || cachedData?.isWorkspacesListStale || cachedData?.isWorkspaceDataStale;

      if (!shouldBackgroundRefresh) {
        setLoadingState('fetchingWorkspaces', true);
      }

      // Fetch user's workspaces (will use cache if valid)
      const userWorkspaces = await fetchWorkspaces();

      if (userWorkspaces.length === 0) {
        // User has no workspaces - this shouldn't happen but handle it
        clearWorkspace();
        setInitialized(true);
        return;
      }

      // Check if stored workspace is still valid for the current user
      if (storedWorkspaceId) {
        const validWorkspace = userWorkspaces.find(ws => ws.workspace_id === storedWorkspaceId);

        if (validWorkspace) {
          // Check if creator has active subscription
          if (!validWorkspace.creator_has_active_subscription) {
            // Current workspace creator subscription is inactive, find an active one
            const activeWorkspace = userWorkspaces.find(ws => ws.creator_has_active_subscription);

            if (activeWorkspace) {
              // Switch to workspace with active subscription
              setCurrentWorkspace({
                workspace_id: activeWorkspace.workspace_id,
                name: activeWorkspace.name,
                role: activeWorkspace.role
              });

              localStorage.setItem('current_workspace_id', activeWorkspace.workspace_id);
              localStorage.setItem('current_workspace_name', activeWorkspace.name);
              localStorage.setItem('current_user_role', activeWorkspace.role);

              fetchWorkspaceDetails(activeWorkspace.workspace_id, true);
            } else {
              // No workspace with active subscription found
              setNeedsSubscriptionExpiredRedirect(true);
            }
          } else {
            // Update with fresh data if not already set from cache
            if (!currentWorkspace || currentWorkspace.workspace_id !== validWorkspace.workspace_id) {
              setCurrentWorkspace({
                workspace_id: validWorkspace.workspace_id,
                name: validWorkspace.name,
                role: validWorkspace.role
              });
            }

            localStorage.setItem('current_workspace_name', validWorkspace.name);
            localStorage.setItem('current_user_role', validWorkspace.role);

            // Always fetch workspace details to ensure we have fresh data
            // This ensures get-workspace is called on reload
            fetchWorkspaceDetails(validWorkspace.workspace_id, shouldBackgroundRefresh || !workspaceData);
          }
        } else {
          // Stored workspace no longer valid, find first available with active subscription
          localStorage.removeItem('current_workspace_id');
          localStorage.removeItem('current_workspace_name');
          localStorage.removeItem('current_user_role');

          const activeWorkspace = userWorkspaces.find(ws => ws.creator_has_active_subscription);

          if (activeWorkspace) {
            // Set it directly without using switchWorkspace to avoid API call
            setCurrentWorkspace({
              workspace_id: activeWorkspace.workspace_id,
              name: activeWorkspace.name,
              role: activeWorkspace.role
            });

            // Store in localStorage
            localStorage.setItem('current_workspace_id', activeWorkspace.workspace_id);
            localStorage.setItem('current_workspace_name', activeWorkspace.name);
            localStorage.setItem('current_user_role', activeWorkspace.role);

            // Fetch workspace details
            fetchWorkspaceDetails(activeWorkspace.workspace_id, true);
          } else {
            // No workspace with active subscription found
            setNeedsSubscriptionExpiredRedirect(true);
          }
        }
      } else {
        // No stored workspace, use the first available with active subscription
        const activeWorkspace = userWorkspaces.find(ws => ws.creator_has_active_subscription);

        if (activeWorkspace) {
          // Set it as current workspace
          setCurrentWorkspace({
            workspace_id: activeWorkspace.workspace_id,
            name: activeWorkspace.name,
            role: activeWorkspace.role
          });

          // Store in localStorage
          localStorage.setItem('current_workspace_id', activeWorkspace.workspace_id);
          localStorage.setItem('current_workspace_name', activeWorkspace.name);
          localStorage.setItem('current_user_role', activeWorkspace.role);

          // Always fetch workspace details to ensure we have fresh data
          fetchWorkspaceDetails(activeWorkspace.workspace_id, true); // Force refresh for new default workspace
        } else {
          // No workspace with active subscription found
          setNeedsSubscriptionExpiredRedirect(true);
        }
      }

      setInitialized(true);
    } catch (error) {
      setError('Failed to initialize workspace');
      setInitialized(true);
    } finally {
      if (!isUsingCachedData) {
        setLoadingState('fetchingWorkspaces', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWorkspaces, fetchWorkspaceDetails, clearWorkspace, isUsingCachedData]);

  // Check if user has access to a workspace
  const hasWorkspaceAccess = useCallback((workspaceId) => {
    return workspaces.some(ws => ws.workspace_id === workspaceId);
  }, [workspaces]);

  // Get user's role in current workspace
  const getCurrentRole = useCallback(() => {
    return currentWorkspace?.role || null;
  }, [currentWorkspace]);

  // Check if user is admin in current workspace
  const isWorkspaceAdmin = useCallback(() => {
    return currentWorkspace?.role === 'admin';
  }, [currentWorkspace]);

  // Workspace limit utility functions
  const getWorkspaceLimitInfo = useCallback((limitType, currentValue = null) => {
    if (!workspaceData) {
      return {
        current: 0,
        limit: 0,
        remaining: 0,
        percentage: 0,
        hasReached: false,
        canCreate: false,
        isLoading: true
      };
    }

    let current, limit;

    switch (limitType) {
      case 'workspace_count':
        // For workspace count, we use the total workspace count from workspaces list
        // Note: workspace_count limits come from user limits, not workspace data
        // This case is handled here for completeness but should use UserContext for actual user-level limits
        current = workspaces.length || 0;
        limit = 0; // User-level limits are not available in workspace data
        break;
      case 'workspace_agent':
        current = currentValue !== null ? currentValue : (workspaceData.workspace_bv_count || 0);
        limit = workspaceData.workspace_bv_count_limit || 0;
        break;
      case 'workspace_character':
        current = currentValue !== null ? currentValue : (workspaceData.workspace_character_count || 0);
        limit = workspaceData.workspace_character_count_limit || 0;
        break;
      case 'workspace_message':
        current = currentValue !== null ? currentValue : (workspaceData.workspace_message_count || 0);
        limit = workspaceData.workspace_message_count_limit || 0;
        break;
      case 'workspace_member':
        current = currentValue !== null ? currentValue : (workspaceData.workspace_member_count || 0);
        limit = workspaceData.workspace_member_count_limit || 0;
        break;
      case 'workspace_crawl_url':
        current = currentValue !== null ? currentValue : (workspaceData.workspace_crawl_url_count || 0);
        limit = workspaceData.workspace_crawl_url_count_limit || 0;
        break;
      default:
        current = 0;
        limit = 0;
    }

    // Special handling for workspace_message (credits)
    // Credits start at limit and decrement to 0, so current = remaining credits
    let remaining, percentage, hasReached, canCreate;

    if (limitType === 'workspace_message') {
      remaining = current; // Current value IS the remaining credits
      percentage = limit > 0 ? Math.min(100, Math.round(((limit - current) / limit) * 100)) : 0; // Percentage USED
      hasReached = current <= 0; // No credits left
      canCreate = current > 0; // Can create if credits remain
    } else {
      // Standard handling for other limits (increment from 0 to limit)
      remaining = Math.max(0, limit - current);
      percentage = limit > 0 ? Math.min(100, Math.round((current / limit) * 100)) : 0;
      hasReached = current >= limit;
      canCreate = !hasReached;
    }

    return {
      current,
      limit,
      remaining,
      percentage,
      hasReached,
      canCreate,
      isLoading: false
    };
  }, [workspaceData, workspaces]);

  // Check if workspace has reached a specific limit
  const hasReachedWorkspaceLimit = useCallback((limitType, currentValue = null) => {
    const limitInfo = getWorkspaceLimitInfo(limitType, currentValue);
    return limitInfo.hasReached;
  }, [getWorkspaceLimitInfo]);

  // Get remaining capacity for a workspace limit
  const getWorkspaceRemainingCapacity = useCallback((limitType, currentValue = null) => {
    const limitInfo = getWorkspaceLimitInfo(limitType, currentValue);
    return limitInfo.remaining;
  }, [getWorkspaceLimitInfo]);

  // Get usage percentage for a workspace limit
  const getWorkspaceUsagePercentage = useCallback((limitType, currentValue = null) => {
    const limitInfo = getWorkspaceLimitInfo(limitType, currentValue);
    return limitInfo.percentage;
  }, [getWorkspaceLimitInfo]);

  // Get just workspace (includes state and basic workspace info)
  const getJustWorkspace = useCallback(async (updateContext = false) => {
    try {
      const token = localStorage.getItem('brandvox_token');
      if (!token || !currentWorkspace) {
        return null;
      }

      const response = await fetch(`${apiUrl}/api/v1/workspace/get-just-workspace?workspace_id=${currentWorkspace.workspace_id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const workspace = data.workspace;

        // Update workspace data in context if requested
        if (updateContext && workspace && workspaceData) {
          setWorkspaceData(prevData => ({
            ...prevData,
            state: workspace.state,
            workspace_bv_count: workspace.workspace_bv_count,
            workspace_character_count: workspace.workspace_character_count,
            workspace_crawl_url_count: workspace.workspace_crawl_url_count,
            workspace_member_count: workspace.workspace_member_count,
            workspace_message_count: workspace.workspace_message_count,
            quick_start_guide: workspace.quick_start_guide,
          }));
        }

        return workspace;
      }
      return null;
    } catch (error) {
      return null;
    }
  }, [apiUrl, currentWorkspace, workspaceData]);

  // Get workspace state (backward compatibility)
  const getWorkspaceState = useCallback(async () => {
    try {
      const workspace = await getJustWorkspace();
      return workspace?.state || null;
    } catch (error) {
      return null;
    }
  }, [getJustWorkspace]);

  // Update chatbot states in workspace data
  const updateChatbotStates = useCallback((updatedStates) => {
    setWorkspaceData(prevData => {
      if (!prevData?.chatbots) return prevData;

      const updatedChatbots = prevData.chatbots.map(chatbot => ({
        ...chatbot,
        state: updatedStates[chatbot.chatbot_id]?.state || chatbot.state
      }));

      return {
        ...prevData,
        chatbots: updatedChatbots
      };
    });
  }, []); // Empty dependency array to keep function reference stable

  // Check if workspace has access to a feature
  const hasWorkspaceFeatureAccess = useCallback((feature) => {
    if (!workspaceData) return false;

    switch (feature) {
      case 'white_label':
        return workspaceData.workspace_bv_white_label_enabled || false;
      case 'advanced_ai_models':
        return workspaceData.workspace_allowed_ai_model_options && workspaceData.workspace_allowed_ai_model_options.length > 0;
      case 'website_sync':
        return workspaceData.workspace_website_sync_options && workspaceData.workspace_website_sync_options.length > 0;
      default:
        return true; // Default to allowed for unknown features
    }
  }, [workspaceData]);

  // Auto-initialize on mount and when authentication changes
  useEffect(() => {
    const token = localStorage.getItem('brandvox_token');
    const userEmail = localStorage.getItem('user_email');

    // Check if user has changed (different email)
    if (userEmail && userEmail !== lastUserEmail) {
      // Clear workspace context when user changes
      clearWorkspace();
      setInitialized(false);
      setLastUserEmail(userEmail);

      // Initialize for the new user
      if (token) {
        initializeWorkspace();
      }
      return;
    }

    if (token && !initialized && !loadingStates.fetchingWorkspaces) {
      // User is logged in but workspace not initialized and not currently fetching
      initializeWorkspace();
    } else if (!token && initialized) {
      // User logged out, clear workspace
      clearWorkspace();
      setInitialized(false);
      setLastUserEmail(null);
    }
  }, [isLoggedIn, initialized, initializeWorkspace, clearWorkspace, loadingStates.fetchingWorkspaces, lastUserEmail]);

  const value = {
    currentWorkspace,
    workspaceData,
    workspaces,
    loading, // Computed loading state for backward compatibility
    loadingStates, // Individual loading states for each operation
    error,
    initialized,
    isUsingCachedData,
    needsSubscriptionExpiredRedirect,
    fetchWorkspaces,
    switchWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    clearWorkspace,
    initializeWorkspace,
    checkWorkspaceSubscriptions,
    hasWorkspaceAccess,
    getCurrentRole,
    isWorkspaceAdmin,
    fetchWorkspaceDetails,
    clearError,
    updateChatbotStates,
    getWorkspaceState,
    getJustWorkspace,
    // Workspace limit functions
    getWorkspaceLimitInfo,
    hasReachedWorkspaceLimit,
    getWorkspaceRemainingCapacity,
    getWorkspaceUsagePercentage,
    hasWorkspaceFeatureAccess,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export default WorkspaceContext;